/** RFC 3 - in-memory access token; refresh via HttpOnly cookie + CSRF. */

let accessToken: string | null = null;
let organizationId: string | null = null;

export const getAccessToken = (): string | null => accessToken;

export const setAccessToken = (token: string | null): void => {
  accessToken = token;
};

export const getOrganizationId = (): string | null => organizationId;

export const setOrganizationId = (id: string | null): void => {
  organizationId = id;
};

export const clearSession = (): void => {
  accessToken = null;
  organizationId = null;
};

/**
 * Clear in-memory auth state and the SPA-readable CSRF cookie.
 * HttpOnly refresh cookie is cleared only by the API (logout / rotated Set-Cookie).
 */
export const clearReadableSessionArtifacts = (): void => {
  clearSession();
  document.cookie = "agencyos_csrf=; Max-Age=0; Path=/";
};

export const readCsrfToken = (): string | null => {
  const match = /(?:^|;\s*)agencyos_csrf=([^;]*)/u.exec(document.cookie);
  if (match?.[1] === undefined) {
    return null;
  }
  return decodeURIComponent(match[1]);
};

/**
 * Read `org` claim from a JWT payload without verifying the signature.
 * Server remains the authority on every authenticated request.
 */
export const readOrganizationIdFromAccessToken = (token: string): string | null => {
  const parts = token.split(".");
  if (parts.length < 2) {
    return null;
  }
  const segment = parts[1];
  if (segment === undefined || segment.length === 0) {
    return null;
  }
  try {
    const normalized = segment.replace(/-/gu, "+").replace(/_/gu, "/");
    const padLen = (4 - (normalized.length % 4)) % 4;
    const json = atob(`${normalized}${"=".repeat(padLen)}`);
    const parsed: unknown = JSON.parse(json);
    if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) {
      return null;
    }
    const org = (parsed as { readonly org?: unknown }).org;
    return typeof org === "string" && org.length > 0 ? org : null;
  } catch {
    return null;
  }
};

/** Store access token and mirror active organization from the JWT `org` claim. */
export const applyAccessToken = (token: string): void => {
  setAccessToken(token);
  setOrganizationId(readOrganizationIdFromAccessToken(token));
};
